import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFetchSingleMediaQuery } from '../../../../contentTypes/mediaApiSlice';
import { setSingleMedia } from '../../../../contentTypes/mediaSlice';
import { AuthContext } from '../../../context/auth-context';
import { checkChallengeSecionId } from '../../../util/challenges';
import { SlideContent, PromotionContent, TakeActionContent } from '../types';
import { getCategoryFromContent, getPromoUrl } from '../utils';

type Language = 'en' | 'de' | 'sv';

type TitleObject = { [key: string]: { text?: string } | string };

export const useContentItem = (
  slideContent: SlideContent | PromotionContent | TakeActionContent
) => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const location = useLocation();
  const dispatch = useDispatch();
  const [isClicked, setIsClicked] = useState(false);

  const chid =
    'media' in slideContent && slideContent.media?.chid
      ? slideContent.media.chid
      : slideContent.chid;

  const mediaId = slideContent?.chid?.startsWith('TA') ? slideContent._id : chid;

  const { data: media, isLoading } = useFetchSingleMediaQuery(
    {
      token: auth?.token,
      url: `/${getCategoryFromContent(slideContent)}/${mediaId}`,
    },
    { skip: !isClicked }
  );

  useEffect(() => {
    if (!isLoading && media && isClicked) {
      setIsClicked(false);
      dispatch(setSingleMedia(media));
      const categoryUrl = getCategoryFromContent(media);

      navigate(`/${categoryUrl}/${chid}/${media.title.replace(/ /g, '_')}`);
    }
  }, [media, isLoading, isClicked, dispatch, navigate, slideContent, chid]);

  const openMediaModal = (e) => {
    e.preventDefault();
    if (
      !(
        location.pathname.startsWith('/media') ||
        location.pathname.startsWith('/take-actions') ||
        location.pathname.startsWith('/programme') ||
        location.pathname.startsWith('/masterclass')
      )
    ) {
      sessionStorage.setItem(
        'previousLocation',
        JSON.stringify({ pathname: location.pathname, scrollY: window.scrollY })
      );
    }
    const { url } = slideContent as SlideContent | PromotionContent;
    if (slideContent.chid.startsWith('PB') && url) {
      if (url.endsWith('/challenge')) {
        const partsArray = url.split('/');
        if (
          partsArray.length < 3 ||
          partsArray[partsArray.length - 1] !== 'challenge' ||
          checkChallengeSecionId(partsArray[partsArray.length - 2])
        ) {
          return;
        }
        navigate(`/${partsArray[partsArray.length - 2]}/${partsArray[partsArray.length - 1]}`);
      } else {
        window.open(url, '_blank');
      }
    } else if (slideContent.chid.startsWith('PB') || slideContent.chid.startsWith('TA')) {
      setIsClicked(true);
    } else {
      const categoryUrl = getCategoryFromContent(slideContent);

      navigate(`/${categoryUrl}/${chid}/${slideContent.title.replace(/ /g, '_')}`);
    }
  };

  const extractText = (input: any, language: Language = 'en'): string => {
    let rawText: string = '';

    const isObject = typeof input === 'object' && input !== null;
    const value = isObject ? (input as TitleObject)[language] : null;
    const isStringValue = typeof value === 'string';
    const isObjectValue = typeof value === 'object' && value !== null && 'text' in value;

    if (isStringValue) {
      rawText = value;
    } else if (isObjectValue) {
      rawText = (value.text as string) || '';
    } else {
      rawText = input as string;
    }

    return rawText;
  };

  const getTileLink = () => {
    const promoUrl = getPromoUrl(slideContent);

    if (promoUrl) return promoUrl;

    const categoryUrl = getCategoryFromContent(slideContent);

    return `/${categoryUrl}/${chid}/${extractText(slideContent.title).replace(/ /g, '_')}`;
  };

  return { openMediaModal, getTileLink, extractText };
};
